import type { Feature, Polygon as GeoJsonPolygon } from 'geojson';
import { LuMapPinned } from 'react-icons/lu';

import type { UnsurveyedResponse, UnsurveyedSiteProperties } from '~api/services';
import { Polygon, SvgTooltip } from '../components';
import { useLayerTooltip } from '../hooks';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type UnsurveyedSitesLayerProps = {
  /** Unsurveyed sites GeoJSON feature collection */
  unsurveyedSites: UnsurveyedResponse;
};

type UnsurveyedFeature = Feature<GeoJsonPolygon, UnsurveyedSiteProperties>;

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const getUnsurveyedFeatureId = (f: UnsurveyedFeature) => f.properties.name;
const TOOLTIP_CONTENT: { label: string; value: (feature: UnsurveyedFeature) => string }[] = [
  {
    label: 'Is Valid',
    value: (f) => String(f.properties.is_valid),
  },
  {
    label: 'Distance',
    value: (f) => (f.properties.distance != null ? `${f.properties.distance.toFixed(0)} m` : 'N/A'),
  },
  {
    label: 'Vibration Limit',
    value: (f) => (f.properties.vibration_limit != null ? `${f.properties.vibration_limit} mm/s` : 'N/A'),
  },
  {
    label: 'Geotechnical Sensitivity',
    value: (f) => f.properties.geotechnical_sensitivity || 'N/A',
  },
  {
    label: 'Site Assessment',
    value: (f) => f.properties.site_assessment || 'N/A',
  },
];

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Layer to render unsurveyed sites (heritage, geotechnical) as polygons on the canvas.
 *
 * @example
 * ```tsx
 * <UnsurveyedSitesLayer unsurveyedSites={unsurveyedSites} />
 * ```
 */
const UnsurveyedSitesLayer = ({ unsurveyedSites }: UnsurveyedSitesLayerProps) => {
  const { layerRef, tooltipRef, hoveredItem } = useLayerTooltip({
    items: unsurveyedSites.features,
    getId: getUnsurveyedFeatureId,
  });

  if (unsurveyedSites.features.length === 0) return null;

  return (
    <>
      <g className="unsurveyed-sites" ref={layerRef} aria-label="Unsurveyed sites layer">
        {unsurveyedSites.features.map((feature) => (
          <g
            key={feature.properties.name}
            className="unsurveyed-site"
            data-item-id={feature.properties.name}
            aria-label={`Unsurveyed site: ${feature.properties.name}`}
          >
            <Polygon
              className="unsurveyed-site-polygon transition-[fill-opacity] duration-200 ease-in-out hover:cursor-pointer hover:[fill-opacity:0.5]"
              geometry={feature.geometry}
              fill="var(--color-sky-400)"
              fillOpacity={0.15}
              stroke="var(--color-sky-500)"
            />
          </g>
        ))}
      </g>
      <SvgTooltip ref={tooltipRef} visible={!!hoveredItem}>
        {hoveredItem && (
          <div className="flex flex-col">
            <h4 className="flex items-center gap-1 rounded-t-md bg-muted px-3 py-2 font-medium text-base">
              <LuMapPinned size={16} />
              {hoveredItem.properties.name}
            </h4>
            <div className="flex flex-col gap-2 rounded-b-md px-3 py-2">
              {TOOLTIP_CONTENT.map(({ label, value }) => (
                <div key={label} className="flex items-center gap-1 text-sm">
                  <span className="font-bold">{label}:</span>
                  <span>{value(hoveredItem)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </SvgTooltip>
    </>
  );
};
UnsurveyedSitesLayer.displayName = 'UnsurveyedSitesLayer';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { UnsurveyedSitesLayer, type UnsurveyedSitesLayerProps };
